import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LuArrowLeft,
  LuLayers,
  LuPackageCheck,
  LuPenSquare,
  LuScissors,
} from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import {
  CuttingCutRows,
  CuttingPieces,
  cutView,
  namesLines,
} from "../../components/cuttingOrders/CuttingPieces";
import CuttingReceiveList from "../../components/cuttingReceives/CuttingReceiveList";
import DetailAccountInfo from "../../components/materialLines/DetailAccountInfo";
import DetailHeader from "../../components/materialLines/DetailHeader";
import DetailPartyInfo from "../../components/materialLines/DetailPartyInfo";
import DetailSection from "../../components/materialLines/DetailSection";
import MaterialPhoto from "../../components/materialLines/MaterialPhoto";
import OrderStatusBadge from "../../components/materialLines/OrderStatusBadge";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  detailCardClass,
  detailInfoLabelClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  pageTitleClass,
  tableHeadCellClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import {
  clearCurrentCuttingOrder,
  fetchCuttingOrderDetail,
  getCurrentCuttingOrder,
  getCurrentCuttingOrderLoading,
  refreshCuttingOrderDetail,
} from "../../features/cuttingOrders/cuttingOrdersSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { deleteCuttingReceive } from "../../services/cuttingReceive.service";
import { makeReport } from "../../services/download.service";
import {
  formatLength,
  lineAmount,
  lineName,
  rateSourceLabel,
} from "../../static/cuttingLine";
import {
  formatAmount,
  formatGujaratiLongDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { BreadcrumbType } from "../../types/common";
import {
  CuttingOrderItemProps,
  CuttingOrderPlanProps,
  CuttingPieceProps,
} from "../../types/cuttingOrder";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "કટીંગ ઓર્ડર", href: route.cuttingOrders },
  { label: "કટીંગ ઓર્ડર વિગતો" },
];

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const closedBadgeClass = "bg-green-100 text-green-800 hover:bg-green-100";

const totalCellClass = "font-bold text-[#113F67]";

const sum = (
  items: CuttingOrderItemProps[],
  pick: (item: CuttingOrderItemProps) => number
) => items.reduce((total, item) => total + (Number(pick(item)) || 0), 0);

const pendingClass = (item: CuttingOrderItemProps) =>
  (item.pending_qty || 0) > 0 ? "text-orange-600" : "text-gray-400";

const amountOf = (item: CuttingOrderItemProps) =>
  item.rate !== undefined ? lineAmount(item.qty, item.rate) : 0;

const CuttingOrderDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const order = useAppSelector(getCurrentCuttingOrder);
  const isLoading = useAppSelector(getCurrentCuttingOrderLoading);
  const loggingUser = useAppSelector(getLoggingUser);
  const sizeUnit = useAppSelector(getSizeUnit);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.cuttingOrdersUpdate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.cuttingOrdersReport
  );
  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.cuttingRate
  );

  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchCuttingOrderDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentCuttingOrder());
    };
  }, [id, dispatch]);

  const items = useMemo(() => order?.items || [], [order]);
  const receives = order?.receives || [];

  // How the order cut each material, from the party's records replayed in date order.
  const plans = order?.plans || [];

  // Back to where the user came from, or to the order list when the page was opened directly.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(route.cuttingOrders);
    }
  };

  const materialName = (plan: CuttingOrderPlanProps) =>
    plan.material?.name || `#${plan.material_id}`;

  const cutRows = (plan: CuttingOrderPlanProps) =>
    plan.rows.length > 0 ? (
      <CuttingCutRows
        cuts={plan.rows.map((row) => cutView(row, namesLines(plan.rows)))}
        sizeUnit={sizeUnit}
      />
    ) : (
      <span className="text-gray-500">-</span>
    );

  // Piece chips without the word "બાર" ("3600mm × 2"): the user had it removed from this table.
  const chips = (pieces: CuttingPieceProps[]) => (
    <CuttingPieces pieces={pieces} sizeUnit={sizeUnit} hideBarWord />
  );

  const handleReportExport = async () => {
    setIsReportLoading(true);
    const file = await makeReport(`/v1/admin/cutting-orders/${id}/pdf`);
    setIsReportLoading(false);

    if (file) dispatch(updatePdfFile(file));
  };

  // Deleting a receive changes what the order received and maybe its status.
  const handleDeleteReceive = async (receiveId: number) => {
    if (await deleteCuttingReceive(receiveId)) {
      await dispatch(refreshCuttingOrderDetail(Number(id)));
    }
  };

  const photo = (item: CuttingOrderItemProps) => (
    <MaterialPhoto
      name={item.item?.name || "-"}
      imageUrl={item.item?.image_url}
      className="h-10 w-10 shrink-0 rounded-md"
      compact
    />
  );

  const itemText = (item: CuttingOrderItemProps) =>
    lineName(item.item?.name, item.size?.name, sizeUnit);

  // A receive closed the line before all of it came, so nothing is pending on it.
  const closedBadge = (item: CuttingOrderItemProps) =>
    item.closed && (
      <Badge variant="none" className={closedBadgeClass}>
        બંધ
      </Badge>
    );

  const rateText = (item: CuttingOrderItemProps) =>
    item.rate !== undefined && (
      <span className="block text-xs font-normal text-gray-500">
        ₹{formatAmount(item.rate)} • {rateSourceLabel(item.rate_source)}
      </span>
    );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>કટીંગ ઓર્ડર વિગતો</h1>
      </div>

      {isLoading ? (
        <div className={detailCardClass}>
          <LoadingState />
        </div>
      ) : !order ? (
        <div className={cx(detailCardClass, "text-center text-gray-500")}>
          કટીંગ ઓર્ડર ની વિગતો મળી નથી
        </div>
      ) : (
        <div className={detailCardClass}>
          <DetailHeader icon={LuScissors} title="કટીંગ ઓર્ડર" id={order.id}>
            <Button variant="outline" className="max-sm:flex-1" onClick={handleBack}>
              <LuArrowLeft className="h-4 w-4" />
              પાછા જાઓ
            </Button>

            {canReport && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                disabled={isReportLoading}
                onClick={handleReportExport}
              >
                {isReportLoading ? "રિપોર્ટ બની રહ્યો છે..." : "રિપોર્ટ બનાવો"}
              </Button>
            )}

            {canUpdate && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                onClick={() =>
                  navigate(
                    route.cuttingOrderEdit.replace(
                      ":id",
                      order.id as unknown as string
                    )
                  )
                }
              >
                <LuPenSquare className="h-4 w-4" />
                એડિટ કરો
              </Button>
            )}
          </DetailHeader>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>તારીખ:</span>
              <span>{formatGujaratiLongDateTime(order.date)}</span>
            </div>

            <div className="flex items-center gap-2">
              <span className={detailInfoLabelClass}>સ્થિતિ:</span>
              <OrderStatusBadge status={order.status} />
            </div>
          </div>

          <DetailPartyInfo partyId={order.party_id} party={order.party} />

          <DetailSection title="કટીંગ ઓર્ડર આઇટમ્સ ની માહિતી">
            <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
              <Table>
                <TableHeader>
                  <TableRow className={detailTableHeadRowClass}>
                    <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                    <TableHead className={tableHeadCellClass}>ફોટો</TableHead>
                    <TableHead className={tableHeadCellClass}>આઈટમ</TableHead>
                    <TableHead className={tableHeadCellClass}>
                      કટીંગ લંબાઈ
                    </TableHead>
                    <TableHead className={tableHeadCellClass}>મળેલ</TableHead>
                    <TableHead className={tableHeadCellClass}>બાકી</TableHead>
                    {withRates && (
                      <TableHead className={cx(tableHeadCellClass, "text-right")}>
                        રકમ
                      </TableHead>
                    )}
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      જથ્થો
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{photo(item)}</TableCell>
                      <TableCell>
                        <span className="block break-words font-semibold text-[#113F67]">
                          {itemText(item)}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {item.material?.name || "-"}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatLength(item.cut_length, sizeUnit)}
                      </TableCell>
                      <TableCell className="font-semibold text-green-700">
                        {formatQty(item.received_qty)}
                      </TableCell>
                      <TableCell className={cx("font-semibold", pendingClass(item))}>
                        <span className="inline-flex items-center gap-2">
                          {formatQty(item.pending_qty)}
                          {closedBadge(item)}
                        </span>
                      </TableCell>
                      {withRates && (
                        <TableCell className={moneyCellClass}>
                          {formatAmount(amountOf(item))}
                          {rateText(item)}
                        </TableCell>
                      )}
                      <TableCell className="text-right text-lg font-bold">
                        {formatQty(item.qty)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow className="bg-[#113F67]/10 hover:bg-[#113F67]/10">
                    <TableCell colSpan={4} className={cx(totalCellClass, "text-right")}>
                      કુલ:
                    </TableCell>
                    <TableCell className={totalCellClass}>
                      {formatQty(sum(items, (item) => item.received_qty || 0))}
                    </TableCell>
                    <TableCell className={totalCellClass}>
                      {formatQty(sum(items, (item) => item.pending_qty || 0))}
                    </TableCell>
                    {withRates && (
                      <TableCell className={cx(totalCellClass, "text-right")}>
                        {formatAmount(sum(items, amountOf))}
                      </TableCell>
                    )}
                    <TableCell className={cx(totalCellClass, "text-right text-lg")}>
                      {formatQty(sum(items, (item) => item.qty))}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            <div className="space-y-4 sm:hidden">
              {items.map((item, index) => (
                <div key={item.id} className={mobileCardClass}>
                  <div className="flex items-center gap-3 border-b p-4">
                    {photo(item)}
                    <span className="min-w-0">
                      <span className="block break-words font-semibold text-[#113F67]">
                        {itemText(item)}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {item.material?.name || "-"}
                      </span>
                    </span>
                  </div>

                  <div className="space-y-3 p-4 text-sm">
                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>ક્રમ</span>
                      <span className="font-medium">{index + 1}</span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>કટીંગ લંબાઈ</span>
                      <span className="font-medium">
                        {formatLength(item.cut_length, sizeUnit)}
                      </span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>મળેલ / બાકી</span>
                      <span className="inline-flex items-center justify-end gap-2 text-right font-semibold">
                        {closedBadge(item)}
                        <span>
                          <span className="text-green-700">
                            {formatQty(item.received_qty)}
                          </span>
                          {" / "}
                          <span className={pendingClass(item)}>
                            {formatQty(item.pending_qty)}
                          </span>
                        </span>
                      </span>
                    </div>

                    {withRates && (
                      <div className={cardRowClass}>
                        <span className={cardLabelClass}>રકમ</span>
                        <span className={cx(moneyCellClass, "text-right")}>
                          {formatAmount(amountOf(item))}
                          {rateText(item)}
                        </span>
                      </div>
                    )}

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>જથ્થો</span>
                      <span className={moneyCellClass}>{formatQty(item.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <div className={cx(mobileCardClass, "space-y-3 p-4 text-sm")}>
                <div className={cardRowClass}>
                  <span className={totalCellClass}>કુલ નંગ</span>
                  <span className={cx(totalCellClass, "text-right")}>
                    {formatQty(sum(items, (item) => item.qty))}
                  </span>
                </div>
                {withRates && (
                  <div className={cardRowClass}>
                    <span className={totalCellClass}>કુલ રકમ</span>
                    <span className={cx(totalCellClass, "text-right")}>
                      {formatAmount(sum(items, amountOf))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </DetailSection>

          <DetailSection title="મટીરીયલ ની માહિતી" icon={LuLayers}>
            {plans.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                આ ઓર્ડર માં કોઈ મટીરીયલ નથી
              </div>
            ) : (
              <>
                <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
                  <Table>
                    <TableHeader>
                      <TableRow className={detailTableHeadRowClass}>
                        <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                        <TableHead className={tableHeadCellClass}>મટીરીયલ</TableHead>
                        <TableHead className={tableHeadCellClass}>
                          પાર્ટી પાસે હતું
                        </TableHead>
                        <TableHead className={tableHeadCellClass}>મોકલેલ</TableHead>
                        <TableHead className={tableHeadCellClass}>કટીંગ</TableHead>
                        <TableHead className={tableHeadCellClass}>
                          પછી પાર્ટી પાસે
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {plans.map((plan, index) => (
                        <TableRow key={plan.material_id} className="align-top">
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell className="break-words font-semibold text-[#113F67]">
                            {materialName(plan)}
                          </TableCell>
                          <TableCell>{chips(plan.before)}</TableCell>
                          <TableCell>{chips(plan.in)}</TableCell>
                          <TableCell className="min-w-[16rem]">
                            {cutRows(plan)}
                          </TableCell>
                          <TableCell>{chips(plan.after)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="space-y-4 sm:hidden">
                  {plans.map((plan, index) => (
                    <div key={plan.material_id} className={mobileCardClass}>
                      <div className="border-b p-4 font-semibold text-[#113F67]">
                        {materialName(plan)}
                      </div>
                      <div className="space-y-3 p-4 text-sm">
                        <div className={cardRowClass}>
                          <span className={cardLabelClass}>ક્રમ</span>
                          <span className="font-medium">{index + 1}</span>
                        </div>
                        <div className="space-y-1.5">
                          <span className={cx(cardLabelClass, "block")}>
                            પાર્ટી પાસે હતું
                          </span>
                          {chips(plan.before)}
                        </div>
                        <div className="space-y-1.5">
                          <span className={cx(cardLabelClass, "block")}>મોકલેલ</span>
                          {chips(plan.in)}
                        </div>
                        <div className="space-y-1.5">
                          <span className={cx(cardLabelClass, "block")}>કટીંગ</span>
                          {cutRows(plan)}
                        </div>
                        <div className="space-y-1.5">
                          <span className={cx(cardLabelClass, "block")}>
                            પછી પાર્ટી પાસે
                          </span>
                          {chips(plan.after)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DetailSection>

          <DetailSection title="રિસીવ ની માહિતી" icon={LuPackageCheck}>
            <CuttingReceiveList
              embedded
              receives={receives}
              pageNumber={1}
              pageSize={receives.length || 1}
              isLoading={false}
              emptyLabel="આ ઓર્ડર ની હજુ કોઈ રિસીવ નથી"
              handleDeleteReceive={handleDeleteReceive}
            />
          </DetailSection>

          <DetailAccountInfo
            creator={order.creator}
            updater={order.updater}
            createdAt={order.created_at}
            updatedAt={order.updated_at}
          />
        </div>
      )}
    </AppLayout>
  );
};
export default CuttingOrderDetailPage;
